import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, QueryList, Renderer2, signal, SimpleChanges, ViewChild, ViewChildren } from '@angular/core'
import { QuestionAndAnswer, AnswerContext, Source } from '../../../types'
import { MoreQuestionsComponent } from './more-questions/more-questions.component'
import { SourcesComponent } from './sources/sources.component'
import { ActionBarComponent } from './action-bar/action-bar.component'
import { CommonModule } from '@angular/common'
import { StatusMessageComponent } from './status-message/status-message.component'
import { animate, style, transition, trigger } from '@angular/animations'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'
import { StatisticsService } from '../../../services/statistics.service'
import { AnswerComponent } from './answer/answer.component'
import { ConfigService } from '../../../services/config.service'

@Component({
   selector: 'app-question-and-answer',
   standalone: true,
   imports: [CommonModule, MoreQuestionsComponent, SourcesComponent, StatusMessageComponent, ActionBarComponent, TranslocoModule, AnswerComponent],
   templateUrl: './question-and-answer.component.html',
   styles: '',
   animations: [
      trigger('fadeIn', [
         transition(':enter', [
            style({ opacity: 0.2 }),
            animate('200ms ease-in', style({ opacity: 1 })),
         ])
      ])
   ]
})
export class QuestionAndAnswerComponent implements AfterViewInit, OnDestroy, OnChanges {
   @ViewChildren('scrollToMe') scrollToMeEls!: QueryList<ElementRef>
   @ViewChildren('context') contexts!: QueryList<ElementRef>

   @ViewChild(SourcesComponent) sourcesComponent!: SourcesComponent

   @Input() qanda!: QuestionAndAnswer

   regex = /\[([1-5]+(?:,\s*\d+)?)\]/g
   answer!: { line: string, index?: number[] }[]
   selectedSource = signal<Source>({ source: null, index: -1 })
   linkClickListeners: (() => void)[] = []
   streamingCompleted = false

   // TODO this get is a bit costly - could be optimized, but it's just a loop over max 5 items...
   //      anyway, this way we don't have to do communicating from the source component back to this component
   get selectedSourceIndex() {
      const idx = this.qanda.answer?.results?.findIndex((s) => s.selected) ?? -2
      return idx + 1
   }

   get isStreaming() {
      return this.config.currentConfig.streamAnswer ?? false
   }

   constructor(private translocoService: TranslocoService, private stats: StatisticsService, private renderer: Renderer2, private config: ConfigService) {
      this.answer = []
   }

   getSourceTitle(index: number) {
      const title = this.qanda.answer?.results?.[index -1]?.title

      if (title) {
         return this.translocoService.translate('answer.sourcetitle', {index, title})
      }
      return this.translocoService.translate('answer.sourcetitledefault', {index})
   }

   scrolltoRelevantElement() {
      // the logic here is, we have the question, status messages, and the answer.
      // when loading, always go to the latest question or status message, but when there is an answer, scroll to the question
      let elToScrollTo = this.scrollToMeEls?.last.nativeElement
      const classList: DOMTokenList = elToScrollTo?.classList ?? null

      if (classList?.contains('answer')) {
         elToScrollTo = this.scrollToMeEls.first.nativeElement
      }
      // now do it!
      elToScrollTo?.scrollIntoView({ behavior: 'smooth' })
   }

   ngAfterViewInit() {
      this.scrolltoRelevantElement()

      this.scrollToMeEls.changes.subscribe(() => {
         this.scrolltoRelevantElement()
      })

   }

   // for regenerate question
   ngOnChanges(changes: SimpleChanges) {
      if (!changes['qanda']?.firstChange) {
         this.createAnswerWithAnnotations()
         setTimeout(() => {
            this.scrolltoRelevantElement()
            for (const el of this.contexts) {
               this.markTextTruncated(el.nativeElement)
               const links = el.nativeElement.getElementsByTagName('a')
               if(links.length > 0) {
                  for(const link of links) {
                     const listener = this.renderer.listen(link, 'click', () => {
                        this.stats.sendEvent({ category: 'context', action: 'click-link', label: 'link', value: link.href, question: this.qanda.question })
                        if(link.classList.contains('t-context-improved-query-search ')) {
                           // This is a bit ugly, but this is the event from the official 'related' click, copying it here so Primo will track this too
                           this.stats.sendEvent({ category: 'context', action: 'related', label: 'answerId', value: this.qanda.answer?.id, question: this.qanda.question })
                        }
                     })
                     this.linkClickListeners.push(listener)
                  }
               }
            }})
      }
   }

   ngOnDestroy() {
      //Remove click listeners
      this.linkClickListeners.forEach(listener => listener())
   }

   createAnswerWithAnnotations() {
      if (!this.qanda.answer?.answer) {
         return
      }
      this.answer = []

      let m
      let searchIdx = 0

      // eslint-disable-next-line no-cond-assign
      while (m = this.regex.exec(this.qanda.answer.answer)) {
         const number = m[1].split(/, ?/g).map((n) => parseInt(n))
         const partOfAnswerWithAnnotation = this.qanda.answer.answer.substring(searchIdx, m.index)
         // sometimes the part of the answer is just whitespace, then we clear it, but add the index, it might be relevant, hence the trim
         // also if the previous answer array has the same number, and the line is empty, we don't add it
         if(this.answer.length > 1 && this.answer[this.answer.length - 1].line.length === 0 &&  (this.answer[this.answer.length - 1]?.index?.[0] ?? -1) === number[0]) {
            // do nothing
         } else {
            this.answer.push({ line: partOfAnswerWithAnnotation.trim(), index: number })
         }
         searchIdx = this.regex.lastIndex
      }
      // if there is anything at the end, also add it
      if (searchIdx > 0 && searchIdx < this.qanda.answer.answer.length) {
         this.answer.push({ line: this.qanda.answer.answer.substring(searchIdx) })
      }
      if (searchIdx === 0) {
         // Something went wrong parsing the answer, just outputting the answer without annotations
         this.answer.push({line: this.qanda.answer?.answer})
      }
   }

   selectSource(index: number) {
      index = index - 1 // we're accessing an array which is 0-based ¯\_(ツ)_/¯
      if (this.qanda?.answer?.results?.[index]) {
         this.qanda.answer.results.map((msg) => msg.selected = false)
         this.qanda.answer.results[index].selected = true
         this.selectedSource.set({source: this.qanda.answer.results[index], index: index + 1})
         this.stats.sendEvent({ category: 'source', action: 'select-annotation', label: 'sourceId', value: this.qanda.answer.id, question: this.qanda.question })
      }
   }

   readMore($event: MouseEvent, context: AnswerContext) {
      context.readmore = !context.readmore

      this.stats.sendEvent({ category: 'context', action: 'readmore', label: 'answerContext', value: context.message, question: this.qanda.question})
   }

   // this sets a flag on the source if the text is truncated by the line-clamp css
   // because if so then we can show a "read more" button
   markTextTruncated(element: HTMLElement) {
      const truncated = element.scrollHeight > element.clientHeight
      const id = element.attributes.getNamedItem('data-id')?.value
      if (id &&  this.qanda.answer?.context) {
         const idx = parseInt(id.replace('context_', ''))
         const context = this.qanda.answer.context[idx]
         if (context) {
            context.readmore = truncated
         }
      }
   }
}
